// BUILD-046 increment 1b-ii: the Comfortable / Compact choice as two ICON-ONLY buttons, placed at the right end of the tab row, just above the divider line (Owner direction of 2026-10-21).
//
// It replaces the "View" dropdown. One preference for the whole page (densityStore.ts: kept across tabs and reloads, separate from every filter). Each button has an inline SVG icon (no visible
// words), a tooltip and an accessible name ("Comfortable view", "Compact view") from toolbarModel.describeDensityButtons, and `aria-pressed` for the selected one. The selected button is a FILLED dark
// button with a thin inner frame and the other is an outlined white one, so the state shows by shape and brightness, not by hue alone.
import { describeDensityButtons } from './toolbarModel';
import type { Density } from './densityStore';

// Whole class strings (not built from pieces) so the style scanner sees them.
const BUTTON_BASE = 'inline-flex h-8 w-9 items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#2a7fb8]';
const SELECTED = 'bg-gray-800 text-white ring-1 ring-inset ring-white';
const NOT_SELECTED = 'bg-white text-gray-700 hover:bg-gray-100';

const ICON_PROPS = { 'aria-hidden': true, focusable: false, viewBox: '0 0 20 20', className: 'h-4 w-4', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

/** Comfortable: three widely spaced lines, like rows with room around them. */
const COMFORTABLE_ICON = (
  <svg {...ICON_PROPS}>
    <path d="M4 4.5h12M4 10h12M4 15.5h12" />
  </svg>
);

/** Compact: five tight lines, like rows packed together. */
const COMPACT_ICON = (
  <svg {...ICON_PROPS} strokeWidth={1.5}>
    <path d="M4 3.5h12M4 6.75h12M4 10h12M4 13.25h12M4 16.5h12" />
  </svg>
);

export interface DensityButtonsProps {
  density: Density;
  onChange: (next: Density) => void;
}

export function DensityButtons({ density, onChange }: DensityButtonsProps) {
  return (
    <div role="group" aria-label="Row density" className="inline-flex overflow-hidden rounded-md border border-gray-300 bg-white" data-testid="density-buttons">
      {describeDensityButtons(density).map((button, index) => (
        <button
          key={button.value}
          type="button"
          aria-pressed={button.pressed}
          aria-label={button.ariaLabel}
          title={button.title}
          data-testid={`density-${button.value}`}
          onClick={() => onChange(button.value)}
          className={`${BUTTON_BASE} ${index > 0 ? 'border-l border-gray-300' : ''} ${button.pressed ? SELECTED : NOT_SELECTED}`}
        >
          {button.value === 'compact' ? COMPACT_ICON : COMFORTABLE_ICON}
        </button>
      ))}
    </div>
  );
}
